import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Loader2 } from 'lucide-react';
import { useUserPortfolios } from '@/hooks/useUserPortfolios';
import { toast } from 'sonner';

interface AddProductDialogProps {
  onProductAdded: (productData: {
    name: string;
    description?: string;
    product_type: string;
    manufacturer?: string;
    portfolio_id: string;
  }) => Promise<any>;
}

export const AddProductDialog = ({ onProductAdded }: AddProductDialogProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    product_type: 'formulated',
    manufacturer: '',
    portfolio_id: '',
  });

  const { portfolios, loading: portfoliosLoading } = useUserPortfolios();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    if (!formData.portfolio_id) {
      toast.error('Please select a portfolio');
      return;
    }

    setLoading(true);
    try {
      await onProductAdded({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        product_type: formData.product_type,
        manufacturer: formData.manufacturer.trim() || undefined,
        portfolio_id: formData.portfolio_id,
      });

      // Reset form and close dialog
      setFormData({
        name: '',
        description: '',
        product_type: 'formulated',
        manufacturer: '',
        portfolio_id: '',
      });
      setOpen(false);
      toast.success('Product added successfully');
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Failed to add product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-background">
        <DialogHeader>
          <DialogTitle>Add New Product</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Product Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter product name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter product description"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product_type">Product Type</Label>
            <Select
              value={formData.product_type}
              onValueChange={(value) => setFormData({ ...formData, product_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select product type" />
              </SelectTrigger>
              <SelectContent className="bg-background">
                <SelectItem value="formulated">Formulated Product</SelectItem>
                <SelectItem value="raw_material">Raw Material</SelectItem>
                <SelectItem value="finished_good">Finished Good</SelectItem>
                <SelectItem value="service">Service</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="manufacturer">Manufacturer</Label>
            <Input
              id="manufacturer"
              value={formData.manufacturer}
              onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
              placeholder="Enter manufacturer name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="portfolio">Portfolio *</Label>
            <Select
              value={formData.portfolio_id}
              onValueChange={(value) => setFormData({ ...formData, portfolio_id: value })}
              disabled={portfoliosLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={portfoliosLoading ? "Loading portfolios..." : "Select portfolio"} />
              </SelectTrigger>
              <SelectContent className="bg-background">
                {portfolios.map((portfolio) => (
                  <SelectItem key={portfolio.id} value={portfolio.id}>
                    {portfolio.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Product
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};